// components/NumberWithTooltip.tsx
import React from "react";
import { formatNumberIN, formatShort } from "./utils";
import { usePdfMode } from "../pdf/PdfModeContext";

/**
 * Optional prop tooltipComponent:
 * - pass your Tooltip component (like MUI's Tooltip) to get a styled tooltip.
 * - it should accept props: { title: string, children: React.ReactNode, placement?: string, arrow?: boolean }
 *
 * If tooltipComponent is not provided, this component falls back to native `title` attribute.
 */

export interface NumberWithTooltipProps {
  value: number | string | null | undefined;
  // If true show both full and short in-line: "1,23,456 (1.23L)".
  // If false show only short (eg "1.23L").
  showBoth?: boolean;

  // text appended after number, e.g. 'views'
  unit?: string;

  // optional threshold to decide showing tooltip or not; default 1L
  threshold?: number;

  // If you want a custom Tooltip component (like MUI), pass it here
  // Example: tooltipComponent={Tooltip}  where Tooltip is from @mui/material
  tooltipComponent?: any;

  // extra className
  className?: string;
}

const NumberWithTooltip: React.FC<NumberWithTooltipProps> = ({
  value,
  showBoth = false,
  unit = "",
  threshold = 100000,
  tooltipComponent: TooltipComp,
  className = "",
}) => {
  // PDF report: print the exact number that the tooltip would show
  const pdfMode = usePdfMode();
  // handle invalid
  if (value === null || value === undefined || value === "--" || Number.isNaN(Number(value))) {
    return <span className={className}>-- {unit && <span>{unit}</span>}</span>;
  }

  const num = Number(value);
  const full = formatNumberIN(num);     // 1,23,456
  const short = formatShort(num, 2);    // 1.23L or 1.23Cr or 1.50M depending

  // FIXED DISPLAY RULE
  const displayText =
    num < 100000
      ? full                          // below 1L → only comma format
      : showBoth
      ? `${full} (${short})`
      : short;

  const tooltipText = `${full}${unit ? ` ${unit}` : ""}`;   // 1.23L or 1.23Cr or 1.50M depending on utils

  if (pdfMode) {
    return (
      <span className={className}>
        {displayText}
        {displayText !== full && <span style={{ color: '#64748b', fontSize: '0.85em' }}> ({full})</span>}
        {unit ? ` ${unit}` : ""}
      </span>
    );
  }

  // If a Tooltip component (e.g. MUI) is provided: use it
  if (TooltipComp) {
    // TooltipComp is expected to render children and accept a title prop.
    // Works with MUI Tooltip: <Tooltip title="..." arrow><span>...</span></Tooltip>
    return (
      <TooltipComp title={tooltipText} placement="top" arrow>
        <span className={className} style={{ cursor: "default" }}>{displayText}{unit ? ` ${unit}` : ""}</span>
      </TooltipComp>
    );
  }

  // fallback: native browser tooltip using title
  return (
    <span className={className} title={tooltipText} style={{ cursor: "default" }}>
      {displayText}{unit ? ` ${unit}` : ""}
    </span>
  );
};

export default NumberWithTooltip;
